import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { ShareURLButton } from '#/components/ShareURLButton/ShareURLButton';
import { H2 } from '#/components/Text/H2';
import { Underline } from '#/components/Text/Underline';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SalaryApiRecentSubmission } from '#/modules/salary/api/types/SalaryApiRecentSubmission';
import { salariesSurveyRoute } from '#/modules/salary/routing/salary.routes';
import { colors } from '#/theme/theme';

import {
  SalariesRecentSubmissionsACycle,
  SalariesRecentSubmissionsHeading,
  SalariesRecentSubmissionsHolder,
  SalariesRecentSubmissionsSurveyButtonContainer,
  SalariesRecentSubmissionsTableBody,
  SalariesRecentSubmissionsTableBodyContainer,
  SalariesRecentSubmissionsTableCell,
  SalariesRecentSubmissionsTableContainer,
  SalariesRecentSubmissionsTableHeading,
  SalariesRecentSubmissionsWrapper,
} from './RecentSubmissions.styled';

type RecentSubmissionsProps = {
  alreadySubmitted: boolean;
  recentSubmissions: SalaryApiRecentSubmission[];
};

export function RecentSubmissions({
  alreadySubmitted,
  recentSubmissions,
}: RecentSubmissionsProps) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const tableHeading = i18n('{{s}}Recent Salary Submissions{{/s}}', {
    s: <Underline />,
  });

  const shareUrl = {
    en: salariesSurveyRoute.fullPath,
    es: salariesSurveyRoute.fullPath,
    pt: salariesSurveyRoute.fullPath,
  };

  const data = recentSubmissions
    ? recentSubmissions
        .concat(recentSubmissions.slice(0, 4))
        .map((submission) => [
          submission.title,
          submission.salary.toLocaleString(locale, {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }),
          submission.location,
        ])
    : [];

  return (
    <SalariesRecentSubmissionsHolder
      $staticImgPath={appConfig.idealist.staticImgPath}
    >
      <SalariesRecentSubmissionsWrapper>
        <SalariesRecentSubmissionsHeading>
          <H2>{i18n('Help us research pay equity in the nonprofit sector')}</H2>

          <SalariesRecentSubmissionsSurveyButtonContainer>
            {alreadySubmitted ? (
              <ShareURLButton
                buttonText="Share Our Survey"
                shareUrl={shareUrl}
                variant="secondary-white"
                messageOverride={i18n(
                  'I just took the Idealist Salary Survey! Find out what you could be earning at a nonprofit in just a few quick steps.',
                )}
                emailMessageOverride={i18n(
                  'I just took the Idealist Salary Survey! Find out what you could be earning at a nonprofit in just a few quick steps. Start here:',
                )}
                emailSubjectOverride={i18n('Check this out: Idealist Salaries')}
              />
            ) : (
              <Button
                type="link"
                href={salariesSurveyRoute.fullPath}
                variant="secondary-white"
              >
                {i18n('Take Our Survey')}
              </Button>
            )}
          </SalariesRecentSubmissionsSurveyButtonContainer>
        </SalariesRecentSubmissionsHeading>

        <SalariesRecentSubmissionsTableContainer>
          <SalariesRecentSubmissionsTableHeading>
            {tableHeading}
          </SalariesRecentSubmissionsTableHeading>

          <Box
            display="flex"
            fontWeight={600}
            backgroundColor={colors.backgroundGrey}
          >
            <SalariesRecentSubmissionsTableCell
              style={{ width: '65%', color: colors.lightContentGrey }}
            >
              {i18n('Position')}
            </SalariesRecentSubmissionsTableCell>
            <SalariesRecentSubmissionsTableCell
              style={{ width: '15%', color: colors.lightContentGrey }}
            >
              {i18n('Salary')}
            </SalariesRecentSubmissionsTableCell>
            <SalariesRecentSubmissionsTableCell
              style={{ width: '20%', color: colors.lightContentGrey }}
            >
              {i18n('Location')}
            </SalariesRecentSubmissionsTableCell>
          </Box>

          <SalariesRecentSubmissionsTableBodyContainer>
            <SalariesRecentSubmissionsTableBody
              $animationName={SalariesRecentSubmissionsACycle(data)}
              $itemCount={data.length}
            >
              {data.map((item, i) => (
                <Box key={[...item, i].join()} display="flex">
                  <SalariesRecentSubmissionsTableCell style={{ width: '65%' }}>
                    {item[0]}
                  </SalariesRecentSubmissionsTableCell>
                  <SalariesRecentSubmissionsTableCell style={{ width: '15%' }}>
                    {item[1]}
                  </SalariesRecentSubmissionsTableCell>
                  <SalariesRecentSubmissionsTableCell style={{ width: '20%' }}>
                    {item[2]}
                  </SalariesRecentSubmissionsTableCell>
                </Box>
              ))}
            </SalariesRecentSubmissionsTableBody>
          </SalariesRecentSubmissionsTableBodyContainer>
        </SalariesRecentSubmissionsTableContainer>
      </SalariesRecentSubmissionsWrapper>
    </SalariesRecentSubmissionsHolder>
  );
}
